import React, { useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
  faUpload,
  faCopy,
  faTimes,
  faCamera,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import user from "../../Assets/photo/user.png";
import { hexToRgba, uploadFileToS3 } from "../../utils/common";
import { useLoader } from "../../context/LoaderContext";
import BioMeCom from "../../Assets/photo/biome-com.jpg";
import { BiEdit } from "react-icons/bi";

const FormModal = ({
  modal,
  modalForm,
  setModalForm,
  closeModal,
  saveModal,
  userData,
  deleteSocial,
  deleteAlbums,
  uploadPreview,
  setUploadPreview,
}) => {
  const [zoom, setZoom] = useState(1);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const { setLoading } = useLoader();
  async function handleModalFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match("image.*")) {
      alert("Please select an image file.");
      return;
    }
    setLoading(true);
    const fileURL = await uploadFileToS3(file);
    setLoading(false);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadPreview(fileURL);
      setModalForm((f) => ({ ...f, thumbnailUrl: fileURL }));
    };
    reader.readAsDataURL(file);
  }

  async function handleAlbumUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const uploadedFiles = [];
    setLoading(true);

    // const allowedVideoTypes = ["video/mp4", "video/webm"];

    const existingVideo = (modalForm?.media || []).some((m) =>
      m.type.startsWith("video/"),
    );

    for (const file of files) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        toast.error(`File ${file.name} is not a supported type.`);
        continue;
      }

      // if (isVideo && !allowedVideoTypes.includes(file.type)) {
      //   toast.error(
      //     `Video ${file.name} is not supported. Only MP4 and WebM formats are allowed.`
      //   );
      //   continue;
      // }

      if (
        isVideo &&
        (existingVideo ||
          uploadedFiles.some((f) => f.type.startsWith("video/")))
      ) {
        toast.error("You can only upload one video.");
        continue;
      }

      const size = parseFloat((file.size / (1024 * 1024)).toFixed(2)); // MB
      if (
        Number(userData.storage.used) + size >
        Number(userData.storage.total)
      ) {
        toast.error(
          "You have reached your storage limit. Please upgrade to premium plan.",
        );
        setLoading(false);
        return;
      }

      try {
        const fileURL = await uploadFileToS3(file, true);
        uploadedFiles.push({
          url: fileURL,
          name: file.name,
          size,
          type: file.type,
        });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        console.error(err);
      }
    }

    setLoading(false);

    if (uploadedFiles.length) {
      setUploadPreview(uploadedFiles.map((f) => f.url));
      setModalForm((f) => ({
        ...f,
        media: [...(f.media || []), ...uploadedFiles],
      }));
    }
  }

  function copyLink() {
    if (!modalForm.url) return;
    navigator.clipboard?.writeText(modalForm.url).then(
      () => toast.success("Copied to clipboard!"),
      () => toast.error("Copy failed"),
    );
  }

  const handleUploadBannerAndProfile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match("image.*")) {
      alert("Please select an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImg = async (imageSrc, crop) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        resolve(file);
      }, "image/jpeg");
    });
  };

  const handleCropSave = async () => {
    try {
      setLoading(true);
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      const fileURL = await uploadFileToS3(croppedFile);
      setUploadPreview(fileURL);
      setModalForm((f) => ({ ...f, thumbnailUrl: fileURL }));
      await saveModal(fileURL);
    } catch (err) {
      toast.error("Failed to upload image.");
      console.error(err);
    } finally {
      setImageSrc(null);
      setLoading(false);
    }
  };

  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  return (
    <div className="modal" style={{ display: "flex" }}>
      <div className="modal-content">
        <button className="modal-close" onClick={closeModal} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h3 className="modal-title">{modal.type}</h3>

        <div style={{ marginTop: 12 }}>
          {modal.type === "Name" && (
            <>
              <input
                className="form-control"
                value={modalForm.name || ""}
                onChange={(e) =>
                  setModalForm((f) => ({ ...f, name: e.target.value }))
                }
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
              </div>
            </>
          )}

          {modal.type === "Username" && (
            <>
              <input
                className="form-control"
                value={modalForm.nickname || ""}
                onChange={(e) =>
                  setModalForm((f) => ({ ...f, nickname: e.target.value }))
                }
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
              </div>
            </>
          )}

          {modal.type === "Bio" && (
            <>
              <textarea
                className="form-control"
                rows={5}
                value={modalForm.bio || ""}
                onChange={(e) =>
                  setModalForm((f) => ({ ...f, bio: e.target.value }))
                }
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
              </div>
            </>
          )}

          {(modal.type === "Profile Picture" ||
            modal.type === "Banner Image") && (
            <>
              <div
                className="file-upload"
                onClick={() =>
                  document.getElementById("modalFileInput").click()
                }
              >
                <FontAwesomeIcon icon={faCamera} />
                <p>Click to upload image</p>
                <small>JPG or PNG, max 20MB</small>
                <input
                  id="modalFileInput"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleUploadBannerAndProfile}
                />
              </div>
              {imageSrc && (
                <div style={{ position: "relative", height: "400px" }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={modal.type === "Profile Picture" ? 1 / 1 : 2 / 1} // Mobile aspect ratio
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={handleCropSave}>
                  Save
                </button>
              </div>
            </>
          )}

          {modal.type === "Social Link" && (
            <>
              <div className="form-group">
                <label className="form-label">Platform</label>
                <select
                  className="form-control"
                  value={modalForm.type || "instagram"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      type: e.target.value,
                    }))
                  }
                >
                  <option value="instagram">Instagram</option>
                  <option value="twitter">Twitter</option>
                  <option value="facebook">Facebook</option>
                  <option value="youtube">YouTube</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="tiktok">TikTok</option>
                  <option value="snapchat">Snapchat</option>
                  <option value="website">Website</option>
                  <option value="spotify">Spotify</option>
                  <option value="whatsapp">Whatsapp</option>
                  <option value="lemon8">Lemon8</option>
                  <option value="shop">Shop</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">URL</label>
                <input
                  className="form-control"
                  value={modalForm.link || ""}
                  onChange={(e) =>
                    setModalForm((f) => ({ ...f, link: e.target.value }))
                  }
                  placeholder="https://"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Label (optional)</label>
                <input
                  className="form-control"
                  value={modalForm.label || ""}
                  onChange={(e) =>
                    setModalForm((f) => ({ ...f, label: e.target.value }))
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">Thumbnail (optional)</label>
                <div
                  className="file-upload"
                  onClick={() =>
                    document.getElementById("modalThumbnailInput").click()
                  }
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <p>Click to upload thumbnail</p>
                  <input
                    id="modalThumbnailInput"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleModalFileChange}
                  />
                </div>
                {modalForm.thumbnailUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={modalForm.thumbnailUrl}
                      alt="thumb"
                      style={{ width: 80, height: 80, borderRadius: 8 }}
                    />
                  </div>
                )}
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
                {typeof modalForm.index === "number" && (
                  <button
                    className="btn btn-outline"
                    onClick={() => deleteSocial(modalForm._id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}

          {modal.type === "Albums" && (
            <>
              <div className="form-group">
                <label className="form-label">Album Name (optional)</label>
                <input
                  className="form-control"
                  value={modalForm.name || ""}
                  onChange={(e) =>
                    setModalForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder=""
                />
              </div>

              <div className="form-group">
                <label className="form-label">Add media</label>
                <div
                  className="file-upload"
                  onClick={() =>
                    document.getElementById("mdiaModalInput").click()
                  }
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <p>Click to upload media</p>
                  <input
                    id="mdiaModalInput"
                    type="file"
                    accept="image/*,video/*"
                    style={{ display: "none" }}
                    onChange={handleAlbumUpload}
                  />
                </div>
                {modalForm?.media?.length > 0 &&
                  (modalForm.media?.filter((item) =>
                    item?.type?.startsWith("video/"),
                  )?.length > 0 ? (
                    <div style={{ marginTop: 8 }}>
                      {modalForm.media.map((item, idx) => (
                        <div
                          className="position-relative"
                          style={{ marginTop: 8 }}
                        >
                          <video width="100%" height="auto" controls>
                            <source src={item?.url} type={item?.type} />
                          </video>
                          <div
                            className="theme-lock"
                            onClick={() => {
                              setModalForm((f) => ({
                                ...f,
                                media: f.media.filter(
                                  (m, mIdx) => mIdx !== idx,
                                ),
                              }));
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="theme-selector" style={{ marginTop: 8 }}>
                      {modalForm.media.map((item, idx) => (
                        <div className="theme-option" style={{ marginTop: 8 }}>
                          <img
                            src={item?.url}
                            alt="thumb"
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: 8,
                            }}
                          />
                          <div
                            className="theme-lock"
                            onClick={() => {
                              setModalForm((f) => ({
                                ...f,
                                media: f.media.filter(
                                  (m, mIdx) => mIdx !== idx,
                                ),
                              }));
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
                {typeof modalForm.index === "number" && (
                  <button
                    className="btn btn-outline"
                    onClick={() => deleteAlbums(modalForm._id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}

          {modal.type === "Share" && (
            <>
              <div className="text-center">
                <div style={{ position: "relative", display: "inline-block" }}>
                  <QRCodeSVG
                    value={modalForm?.url}
                    size={256}
                    level={"H"}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                  />
                  <img
                    src={BioMeCom}
                    alt="logo"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "40%",
                    }}
                  />
                </div>
              </div>
              <div className="share-link" style={{ marginTop: 12 }}>
                <input
                  type="text"
                  value={modalForm.url || ""}
                  readOnly
                  className="form-control"
                />
                <button className="btn" onClick={copyLink}>
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
              <div className="share-buttons" style={{ marginTop: 12 }}>
                <a
                  className="share-button facebook"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    modalForm.url || "",
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  className="share-button twitter"
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                    modalForm.url || "",
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fab fa-twitter"></i>
                </a>
                <a
                  className="share-button instagram"
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  className="share-button whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(
                    modalForm.url || "",
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
              </div>
            </>
          )}

          {(modal.type === "Social Link Theme" ||
            modal.type === "Page Design Theme") && (
            <>
              {modal.type === "Social Link Theme" && (
                <>
                  <p className="mb-4 text-danger">
                    Changes added will be applied to all links widgets in your
                    page.
                  </p>

                  <p className="mb-1">Link preview: </p>
                  <a
                    className="social-link mb-3"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      backgroundColor: hexToRgba(
                        modalForm?.background,
                        modalForm?.backgroundOpacity,
                      ),
                      border: `${modalForm?.backgroundBorderWidth || 0}px solid ${modalForm?.backgroundBorder}`,
                      borderRadius: `${modalForm?.backgroundRadius || 0}px`,
                      color: modalForm?.titleColor,
                    }}
                  >
                    <img
                      src={user}
                      alt={`user`}
                      className="social-thumbnail"
                      style={{
                        borderRadius: `${modalForm?.thumbnailRadius}px`,
                      }}
                    />
                    <i
                      className={`social-platform-icon fab fa-facebook`}
                      style={{ color: modalForm?.iconColor }}
                    ></i>
                    <div className="social-info">
                      <div
                        className="social-label"
                        style={{ color: modalForm?.titleColor }}
                      >
                        Facebook URL
                      </div>
                      <div
                        className="social-url"
                        style={{ color: modalForm?.subtitleColor }}
                      >
                        www.facebook.com/yourprofile
                      </div>
                    </div>
                    <button
                      className="btn btn-outline edit-btn"
                      style={{
                        backgroundColor: modalForm?.buttonBackground,
                        border: `1px solid ${modalForm?.buttonBorder}`,
                        borderRadius: `${modalForm?.buttonRadius}px`,
                        color: modalForm?.buttonTextColor,
                      }}
                    >
                      <BiEdit size={18} />
                    </button>
                  </a>
                </>
              )}
              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Background :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.background || "#ffffff"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      background: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Background opacity :{" "}
                </label>
                <div className="w-100 d-flex align-items-center gap-2">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={modalForm.backgroundOpacity ?? 1}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundOpacity: parseFloat(e.target.value),
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ height: 40, width: 78 }}
                    min={0}
                    max={1}
                    step={0.01}
                    value={modalForm.backgroundOpacity || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundOpacity: Number(e.target.value),
                      }))
                    }
                    disabled
                    placeholder="e.g. 0.5"
                  />
                </div>
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Border Width :{" "}
                </label>
                <div className="w-100 d-flex align-items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={1}
                    style={{ flex: 1 }}
                    value={modalForm.backgroundBorderWidth || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundBorderWidth: e.target.value,
                      }))
                    }
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ height: 40, width: 78 }}
                    min={0}
                    max={20}
                    step={1}
                    disabled
                    value={modalForm.backgroundBorderWidth || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundBorderWidth: Number(e.target.value),
                      }))
                    }
                    placeholder="e.g. 12"
                  />
                </div>
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Background Border :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.backgroundBorder || "#e5e7eb"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      backgroundBorder: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Title Color :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.titleColor || "#111827"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      titleColor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Subtitle Color :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.subtitleColor || "#4b5563"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      subtitleColor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Social Media Icon Color :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.iconColor || "#2563eb"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      iconColor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Button Icon Color :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.buttonTextColor || "#ffffff"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      buttonTextColor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Button Border :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.buttonBorder || "#2563eb"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      buttonBorder: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Button Background :{" "}
                </label>
                <input
                  type="color"
                  className="form-control"
                  style={{ height: 40 }}
                  value={modalForm.buttonBackground || "#2563eb"}
                  onChange={(e) =>
                    setModalForm((f) => ({
                      ...f,
                      buttonBackground: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Background Corner :{" "}
                </label>
                <div className="w-100 d-flex align-items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={modalForm.backgroundRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundRadius: Number(e.target.value),
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ height: 40, width: 78 }}
                    min={0}
                    max={100}
                    step={1}
                    disabled
                    value={modalForm.backgroundRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        backgroundRadius: Number(e.target.value),
                      }))
                    }
                    placeholder="e.g. 12"
                  />
                </div>
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Thumbnail Corner :{" "}
                </label>
                <div className="w-100 d-flex align-items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={modalForm.thumbnailRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        thumbnailRadius: Number(e.target.value),
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ height: 40, width: 78 }}
                    min={0}
                    max={100}
                    step={1}
                    disabled
                    value={modalForm.thumbnailRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        thumbnailRadius: Number(e.target.value),
                      }))
                    }
                    placeholder="e.g. 12"
                  />
                </div>
              </div>

              <div className="form-group d-flex align-items-center">
                <label
                  className="form-label mb-0"
                  style={{ minWidth: "210px" }}
                >
                  Button Corner :{" "}
                </label>
                <div className="w-100 d-flex align-items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={modalForm.buttonRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        buttonRadius: Number(e.target.value),
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="form-control"
                    style={{ height: 40, width: 78 }}
                    min={0}
                    max={100}
                    step={1}
                    disabled
                    value={modalForm.buttonRadius || 0}
                    onChange={(e) =>
                      setModalForm((f) => ({
                        ...f,
                        buttonRadius: Number(e.target.value),
                      }))
                    }
                    placeholder="e.g. 8"
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary" onClick={saveModal}>
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormModal;
